import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { config } from "../utils/config";

type FooterNavLink = {
  label: string;
  href: string;
  active?: boolean;
};

function FooterLink(
  props: Readonly<{ href: string; children: React.ReactNode; active?: boolean }>
) {
  const isInactive = props.active === false || !props.href || props.href === "#";
  const base = "relative inline-flex items-center text-sm rounded";
  if (isInactive) {
    return (
      <span className={clsx(base, "text-neutral-500 cursor-default select-none")}>
        {props.children}{" "}
        <span className="text-blue-300/70 text-xs  hidden lg:inline">&nbsp;(coming soon)</span>
      </span>
    );
  }
  return (
    <Link
      href={props.href}
      className={clsx(
        base,
        "text-neutral-400 transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950"
      )}
    >
      {props.children}
      <span
        className="pointer-events-none absolute -bottom-0.5 left-0 h-px w-full scale-x-0 bg-blue/60 transition-transform duration-200 ease-out group-hover:scale-x-100"
        aria-hidden="true"
      />
    </Link>
  );
}

function FooterSection(props: Readonly<{ title: string; links: ReadonlyArray<FooterNavLink> }>) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-white">{props.title}</h3>
      <ul className="mt-6 space-y-3">
        {props.links.map(function renderLink(link) {
          return (
            <li key={`${props.title}-${link.label}`} className="group">
              <FooterLink href={link.href} active={link.active}>
                {link.label}
              </FooterLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SocialIcon(props: Readonly<{ href: string; label: string; children: React.ReactNode }>) {
  return (
    <a
      href={props.href}
      aria-label={props.label}
      target="_blank"
      rel="noreferrer"
      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-neutral-300 transition-colors duration-150 hover:text-white hover:border-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue"
    >
      {props.children}
    </a>
  );
}

export default function Footer() {
  const productLinks: FooterNavLink[] = [
    { label: "Problem", href: "/#diagnosis" },
    { label: "Outcomes", href: "/#deliverables" },
    { label: "How It Works", href: "/#sprint" },
    { label: "Integrations", href: "/#integrations" },
    { label: "Founder Fit", href: "/#fit" },
    { label: "Proof", href: "/#proof" },
    { label: "Pricing", href: "/pricing" },
  ];
  const resourcesLinks: FooterNavLink[] = [
    { label: "FAQ", href: "/#faq" },
    { label: "Legal", href: "/legal" },
    { label: "Privacy", href: "/legal/privacy" },
    { label: "Terms", href: "/legal/terms" },
    { label: "Security", href: "/security" },
  ];

  return (
    <footer className="mt-10 bg-neutral-950 text-neutral-300 lg:mt-24">
      {/* CTA Strip */}
      <section
        aria-label="Primary call to action"
        className="relative isolate overflow-hidden border-t border-white/10"
      >
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.035]">
          <div className="absolute left-1/2 top-0 h-[240px] w-[720px] -translate-x-1/2 rounded-full bg-blue blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-6 py-12 lg:py-16">
          <div className="flex flex-col items-center justify-between gap-6 lg:flex-row">
            <div className="text-left">
              <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl flex items-center flex-wrap">
                <Image
                  src="/branding/logo.png"
                  alt="Lucien AI logo"
                  width={42}
                  height={42}
                  className="mr-3 hidden md:inline-block"
                  style={{ width: "auto", height: "auto" }}
                />
                <Image
                  src="/branding/logo.png"
                  alt="Lucien AI logo"
                  width={36}
                  height={36}
                  className="mr-3 md:hidden"
                  style={{ width: "auto", height: "auto" }}
                />{" "}
                Stop guessing what to build, sell, or change next.
              </h2>
              <p className="mt-1 p-0 text-sm text-neutral-400 lg:mt-2">
                Lucien turns scattered buyer signal into approved actions it can execute.
              </p>
            </div>
            <div className="w-full lg:w-fit flex flex-col lg:flex-row items-center gap-3 [&>a]:text-nowrap">
              <Link
                href="/sign-up"
                className="flex w-full lg:w-40 items-center justify-center rounded-lg bg-blue px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors duration-150 hover:bg-blue/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue"
                data-cta="footer-sign-up"
              >
                Start free
              </Link>
              <Link
                href={config.cal.url}
                target="_blank"
                className="flex w-full lg:w-fit items-center justify-center rounded-lg border border-white/15 bg-white/0 px-4 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue"
                data-cta="footer-talk-gtm"
              >
                Talk through my GTM
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main grid */}
      <div className="mx-auto max-w-7xl px-6 pt-0 lg:pt-10 pb-16">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-4 lg:grid-cols-[minmax(140px,0.9fr)_minmax(140px,0.9fr)_minmax(24px,0.2fr)_minmax(0,2.2fr)]">
          <FooterSection title="Product" links={productLinks} />
          <FooterSection title="Resources" links={resourcesLinks} />
          <div />

          {/* Featured card */}
          <div className="col-span-3 md:col-span-3 lg:col-span-1">
            <div className="p-2 lg:p-0">
              <h3 className="text-sm font-semibold text-white">Put Lucien to work</h3>
              <p className="mt-2 text-sm text-neutral-400">
                Share your product, assumptions, and current signal. Lucien will find the next
                move to approve and run.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-6 sm:flex-row">
          <p className="text-xs text-neutral-500">
            &copy; {new Date().getFullYear()} Lucien AI, Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-neutral-400">
            <a href="/legal/terms" className="hover:text-white">
              Terms
            </a>
            <a href="/legal/privacy" className="hover:text-white">
              Privacy
            </a>
            <a href="/security" className="hover:text-white">
              Security
            </a>
          </div>
          <div className="hidden items-center gap-2">
            <SocialIcon href="https://x.com/" label="X (Twitter)">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M18.244 3H21l-6.5 7.432L22 21h-5.594l-4.375-5.313L6.031 21H3.275l7.037-8.05L2 3h5.719l3.955 4.874L18.244 3Zm-1.957 16.125h1.594L7.791 4.781H6.09l10.197 14.344Z" />
              </svg>
            </SocialIcon>
            <SocialIcon href="https://github.com/" label="GitHub">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M12 2C6.477 2 2 6.59 2 12.253c0 4.523 2.865 8.356 6.839 9.713.5.097.682-.22.682-.49 0-.242-.009-.884-.014-1.735-2.782.616-3.369-1.37-3.369-1.37-.454-1.173-1.11-1.485-1.11-1.485-.908-.64.069-.628.069-.628 1.004.072 1.532 1.05 1.532 1.05.892 1.564 2.341 1.113 2.91.851.091-.662.35-1.113.636-1.37-2.22-.258-4.555-1.14-4.555-5.074 0-1.12.385-2.036 1.018-2.753-.103-.258-.441-1.297.096-2.704 0 0 .834-.272 2.732 1.052a9.317 9.317 0 0 1 2.488-.343c.845.004 1.696.117 2.49.343 1.897-1.324 2.73-1.052 2.73-1.052.538 1.407.2 2.446.098 2.704.634.717 1.017 1.633 1.017 2.753 0 3.943-2.338 4.813-4.566 5.066.359.32.678.95.678 1.915 0 1.382-.013 2.497-.013 2.836 0 .272.18.59.688.489C19.138 20.606 22 16.775 22 12.253 22 6.59 17.522 2 12 2Z"
                  clipRule="evenodd"
                />
              </svg>
            </SocialIcon>
            <SocialIcon href="https://www.linkedin.com/" label="LinkedIn">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M6.94 8.6v10.8H3.76V8.6h3.18ZM5.34 4.8a1.84 1.84 0 1 1 0 3.68 1.84 1.84 0 0 1 0-3.68ZM20.24 13.16c0-2.66-1.42-3.9-3.31-3.9-1.53 0-2.22.84-2.6 1.43V8.6H11.2c.04.86 0 10.8 0 10.8h3.13v-6.03c0-.32.02-.63.12-.86.26-.63.87-1.29 1.88-1.29 1.32 0 1.85.98 1.85 2.43v5.75h3.16v-6.24Z" />
              </svg>
            </SocialIcon>
          </div>
        </div>
      </div>
    </footer>
  );
}
